import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { trackEvent, planItem } from "@/components/Analytics";
import { PENDING_KEY } from "@/pages/PaymentSuccess";

const PLAN_INFO = { start: { name: "Start", price: "395" }, pro: { name: "Pro", price: "995" } };

export const IdeaCheckout = ({ idea, plan = "start", route, onBack }) => {
  const info = PLAN_INFO[plan] || PLAN_INFO.start;
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocale();
  const [terms, setTerms] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [loading, setLoading] = useState(false);
  const trial = !user?.trial_used;

  const pay = async () => {
    setLoading(true);
    trackEvent("begin_checkout", { currency: "DKK", value: planItem(plan).price, items: [planItem(plan)] });
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ route, title: idea.title, topic: `${idea.title}. ${idea.description}` }));
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin, trial, return_path: "/idebank" });
      if (data.changed) {
        sessionStorage.removeItem(PENDING_KEY);
        await refresh();
        navigate(route, { state: { topic: `${idea.title}. ${idea.description}` } });
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      sessionStorage.removeItem(PENDING_KEY);
      toast.error(apiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="idea-checkout">
      <div className="rounded-lg border bg-white p-4">
        <p className="font-display font-medium">{trial ? t("Start gratis prøve af {plan}", { plan: info.name }) : t("Opgradér til {plan}", { plan: info.name })}</p>
        <p className="text-sm text-muted-foreground leading-relaxed mt-1" data-testid="idea-checkout-terms-text">
          {trial
            ? t("{price} kr./md. inkl. moms efter 30 dages gratis prøveperiode. Der trækkes ikke betaling nu. Abonnementet fornyes automatisk, indtil det opsiges.", { price: info.price })
            : t("{price} kr./md. inkl. moms. Betalingen trækkes med det samme, og abonnementet fornyes automatisk hver måned, indtil det opsiges.", { price: info.price })}
        </p>
      </div>
      <label className="flex gap-3 items-start cursor-pointer">
        <Checkbox checked={terms} onCheckedChange={(v) => setTerms(v === true)} className="mt-0.5" data-testid="idea-checkout-terms" />
        <span className="text-sm text-muted-foreground leading-relaxed">
          {t("Jeg har læst og accepterer")}{" "}
          <Link to="/handelsbetingelser" target="_blank" className="text-foreground underline underline-offset-4 hover:no-underline">{t("handelsbetingelserne")}</Link>.
        </span>
      </label>
      <label className="flex gap-3 items-start cursor-pointer">
        <Checkbox checked={delivery} onCheckedChange={(v) => setDelivery(v === true)} className="mt-0.5" data-testid="idea-checkout-delivery" />
        <span className="text-sm text-muted-foreground leading-relaxed">
          {t("Jeg accepterer, at levering af den digitale tjeneste påbegyndes med det samme, og at min eventuelle fortrydelsesret bortfalder i det omfang, dette følger af gældende regler om digitale tjenester.")}
        </span>
      </label>
      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
        <Button variant="secondary" onClick={onBack} disabled={loading} data-testid="idea-checkout-back">{t("Tilbage")}</Button>
        <Button onClick={pay} disabled={!terms || !delivery || loading} className="gap-2" data-testid="idea-checkout-pay">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {trial ? t("Start gratis prøve") : t("Fortsæt til betaling")}
        </Button>
      </div>
    </div>
  );
};
